// components/ColumnSelector.jsx
import React, { useState, useRef, useEffect } from 'react';
import { COLUMN_GROUPS } from '../datos/configColumns';

const ColumnSelector = ({ availableColumns, visibleColumns, onToggleColumn, onReorder, onSelectAll, onInitialSelection, alwaysVisibleIds }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Columnas agrupadas por tab, derivadas de configColumns (COLUMN_ORDER + el `tab` de
// cada columna). Antes esta lista estaba hardcodeada acá y ya se había desincronizado una
// vez: 'Avance Financiero %' no matcheaba su grupo y no se renderizaba, sin checkbox.
	const groupedColumns = Object.entries(COLUMN_GROUPS).reduce((acc, [categoria, keys]) => {
		const cols = keys
			.map(key => availableColumns[key])
			.filter(Boolean);
		if (cols.length) acc[categoria] = cols;
		return acc;
	}, {});

	// Contar columnas seleccionadas
	const selectedCount = visibleColumns.length;
	const totalAvailable = Object.values(availableColumns).length;

	return (
		<div className="relative inline-block" ref={dropdownRef}>
			{/* Botón principal */}
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-2 font-medium"
			>
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
				</svg>
				<span>Personalizar columnas</span>
				<span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
					{selectedCount}/{totalAvailable}
				</span>
			</button>

			{/* Dropdown - ahora con mejor posicionamiento */}
			{isOpen && (
				<div className="absolute left-0 mt-3 w-[500px] max-w-[90vw] bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 border border-gray-200 dark:border-gray-700 overflow-hidden">
					{/* Header */}
					<div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-800 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
						<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center justify-between">
							<span>Configurar columnas visibles</span>
							<button
								onClick={() => setIsOpen(false)}
								className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</h3>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							Selecciona las columnas que deseas visualizar en la tabla
						</p>
					</div>

					{/* Body con scroll */}
					<div className="max-h-[60vh] overflow-y-auto p-5 space-y-5">
						{Object.entries(groupedColumns).map(([category, columns]) =>
							columns.length > 0 && (
								<div key={category} className="space-y-2">
									<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-l-3 border-blue-500 pl-2">
										{category}
									</h4>
									<div className="grid grid-cols-2 gap-3 pl-2">
										{columns.map(column => (
											<label
												key={column.id}
												className={`flex items-center space-x-2 p-1.5 rounded-lg cursor-pointer transition-colors ${alwaysVisibleIds?.has(column.id)
														? 'bg-blue-50 dark:bg-blue-900/20 opacity-80'
														: visibleColumns.some(col => col.id === column.id)
															? 'bg-blue-50 dark:bg-blue-900/20'
															: 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
													}`}
											>
												<input
													type="checkbox"
													checked={visibleColumns.some(col => col.id === column.id) || alwaysVisibleIds?.has(column.id)}
													onChange={() => onToggleColumn(column.id)}
													disabled={column.required || alwaysVisibleIds?.has(column.id)}
													className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer"
												/>
												<span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
													{column.label}
													{alwaysVisibleIds?.has(column.id) ? (
														<span className="ml-1 text-xs text-blue-500">(siempre visible)</span>
													) : column.required ? (
														<span className="ml-1 text-xs text-blue-500">(requerida)</span>
													) : null}
												</span>
											</label>
										))}
									</div>
								</div>
							)
						)}
					</div>

					{/* Footer */}
					<div className="bg-gray-50 dark:bg-gray-700/50 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
						<button
							onClick={onReorder}
							className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors font-medium"
						>
							Reordenar
						</button>
						<div className="flex space-x-2">
<button
							onClick={onSelectAll}
							className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
						>
							Seleccionar todo
						</button>
						<button
							onClick={onInitialSelection}
							className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
						>
							Selección inicial
						</button>
							<button
								onClick={() => setIsOpen(false)}
								className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-sm"
							>
								Aplicar
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ColumnSelector;